import { CloseButton, Flex, IconButton, useDisclosure } from '@chakra-ui/react';
import { ArrowUpIcon } from '@ui/components/Icons';
import { Show } from '@ui/components/Show';
import { ConfirmDeleteDialog } from '@ui/features/snap-site-config/screens/edit/components/ConfirmDeleteDialog';
import { startViewTransition } from '@ui/util/view-transitions';
import {
  type FieldArrayPath,
  type FieldValues,
  type UseFieldArrayReturn,
  useFormContext,
  useWatch,
} from 'react-hook-form';

export interface ArrayActionsProps<
  TFieldValues extends FieldValues = FieldValues,
  TFieldArrayName extends
    FieldArrayPath<TFieldValues> = FieldArrayPath<TFieldValues>,
  TKeyName extends string = 'id',
> {
  fieldActions: UseFieldArrayReturn<TFieldValues, TFieldArrayName, TKeyName>;
  index: number;
  /**
   * Whether to show the move buttons (up/down arrows).
   * If false, only the remove button will be shown.
   */
  showMoveButtons?: boolean;
  /**
   * Layout of the actions, either 'vertical' or 'horizontal'.
   * Vertical layout stacks the actions vertically, while horizontal layout aligns them in a row.
   */
  layout?: 'vertical' | 'horizontal';
}

export function ArrayActions<
  TFieldValues extends FieldValues = FieldValues,
  TFieldArrayName extends
    FieldArrayPath<TFieldValues> = FieldArrayPath<TFieldValues>,
  TKeyName extends string = 'id',
>({
  fieldActions,
  index,
  showMoveButtons = true,
  layout = 'vertical',
}: ArrayActionsProps<TFieldValues, TFieldArrayName, TKeyName>) {
  const { remove, swap } = fieldActions;

  const { isOpen, onOpen, onClose } = useDisclosure();

  function handleRemoveRowIntent(e: React.MouseEvent) {
    if (e.shiftKey) {
      remove(index);
    } else {
      onOpen();
    }
  }

  return (
    <>
      <Flex
        direction={layout === 'vertical' ? 'column' : 'row-reverse'}
        alignItems="center"
        gap={2}
        opacity={0.4}
        _groupHover={{ opacity: 1 }}
        transition="opacity 0.2s"
      >
        <CloseButton
          aria-label="Remove Row"
          onClick={handleRemoveRowIntent}
          transition="opacity 0.2s"
          title="Remove Row"
        />

        <Show when={showMoveButtons}>
          <IconButton
            size="sm"
            variant="ghost"
            aria-label="Move Row Up"
            icon={<ArrowUpIcon />}
            onClick={() => startViewTransition(() => swap(index, index - 1))}
            isDisabled={index <= 0}
            opacity={0}
            _disabled={{ opacity: 0 }}
            _groupHover={{ opacity: 1, _disabled: { opacity: 0.4 } }}
            transition="opacity 0.2s"
            title="Move Row Up"
          />
        </Show>

        <Show when={showMoveButtons}>
          <IconButton
            size="sm"
            variant="ghost"
            aria-label="Move Row Down"
            icon={<ArrowUpIcon transform="rotate(180deg)" />}
            onClick={() => startViewTransition(() => swap(index, index + 1))}
            isDisabled={index >= fieldActions.fields.length - 1}
            opacity={0}
            _disabled={{ opacity: 0 }}
            _groupHover={{ opacity: 1, _disabled: { opacity: 0.4 } }}
            transition="opacity 0.2s"
            title="Move Row Down"
          />
        </Show>
      </Flex>

      <ConfirmDeleteDialog
        isOpen={isOpen}
        onDismiss={onClose}
        onCancel={onClose}
        onConfirm={() => {
          remove(index);
          onClose();
        }}
      />
    </>
  );
}

export function EnabledArrayActions<
  TFieldValues extends FieldValues = FieldValues,
  TFieldArrayName extends
    FieldArrayPath<TFieldValues> = FieldArrayPath<TFieldValues>,
  TKeyName extends string = 'id',
>({
  enabledPath,
  fieldActions,
  index,
}: ArrayActionsProps<TFieldValues, TFieldArrayName, TKeyName> & {
  enabledPath: string;
}) {
  const form = useFormContext();
  const isEnabled = useWatch({
    name: enabledPath,
    control: form.control,
  });

  return (
    <ArrayActions
      fieldActions={fieldActions}
      index={index}
      showMoveButtons={isEnabled}
    />
  );
}
